// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// What this device calls "launch the ball".
//
// THE GAP THIS CLOSES
// -------------------
// One `machine.json` is read by three hosts, and the plunger is a different gesture in each: a
// phone drags it down the screen, a Quest player reaches out and pulls it, and a desktop holds
// the space bar. NEON NOVA's attract marquee used to read "PRESS START" everywhere, which on a
// phone is an instruction to find a button that does not exist on the screen.
//
// So the artwork carries a `{startHint}` token and the host supplies the words. This module is
// the browser's answer; `DmdPlayer.StartHint` in the Quest runtime is the headset's.
//
// WHY IT IS MEASURED AND NOT SNIFFED
// ----------------------------------
// 08 §6's rule for the whole client is that capability is measured, never read off a user-agent
// string, and `pointer.ts` already owns the queries. A tablet is a phone for this purpose and a
// laptop with a touchscreen is still a keyboard — `coarsePointer` draws that line in exactly the
// place this question needs, which is why it is reused rather than re-asked.

import { DEFAULT_START_HINT } from '../fx/dmd'
import { coarsePointer } from './pointer'

/**
 * The wording for a touchscreen: the plunger is dragged, and *down* is the part nobody guesses.
 *
 * The same lesson `keyGuide.ts` records about the space bar — launch power is how far it travels,
 * so a player who taps fires the weakest shot in the game for ever and never finds out why.
 */
export const TOUCH_START_HINT = 'PULL THE PLUNGER DOWN TO START'

/** The wording for a keyboard. Named as a key, because that is what a player is looking for. */
export const KEYBOARD_START_HINT = 'PRESS SPACE BAR TO START'

/**
 * How this browser should say it.
 *
 * @param pointerIsCoarse Injected so a test can ask both answers without a DOM. Defaults to the
 *   measured value, which is the only thing production ever passes.
 */
export function startHintFor(pointerIsCoarse: boolean = coarsePointer()): string {
  return pointerIsCoarse ? TOUCH_START_HINT : KEYBOARD_START_HINT
}

/** Re-exported so a caller needs one import to have a safe fallback as well as the answer. */
export { DEFAULT_START_HINT }
